/**
 * Short-term enemy spawn heat memory for adjutant prediction.
 * Experiential samples only — no wave-script / north-wall prior cheat.
 */
export type SpawnHeatSample = { x: number; y: number; t: number };

export type SpawnHeatState = {
  samples: SpawnHeatSample[];
};

export type SpawnHeatQuery = {
  com: { x: number; y: number } | null;
  lateralSigma: number;
  confidence: number;
  /** Sum of undecayed weights (pre softCap). */
  totalWeight: number;
};

const MAX_SAMPLES = 32;
const DEFAULT_HALF_LIFE = 10;
const DEFAULT_SOFT_CAP = 6;

export function createSpawnHeat(): SpawnHeatState {
  return { samples: [] };
}

export function noteEnemySpawn(
  heat: SpawnHeatState,
  time: number,
  x: number,
  y: number,
): void {
  heat.samples.push({ x, y, t: time });
  if (heat.samples.length > MAX_SAMPLES) {
    heat.samples.splice(0, heat.samples.length - MAX_SAMPLES);
  }
}

function halfLifeSec(): number {
  return DEFAULT_HALF_LIFE;
}

function softCap(): number {
  return DEFAULT_SOFT_CAP;
}

/** Weight of a sample at `now` (exponential decay). */
export function sampleWeight(
  sample: SpawnHeatSample,
  now: number,
  halfLife = halfLifeSec(),
): number {
  const age = Math.max(0, now - sample.t);
  const hl = Math.max(0.5, halfLife);
  return Math.pow(0.5, age / hl);
}

/**
 * Weighted COM + lateral sigma about the COM's dominant axis (x if wider
 * east-west, else y). Confidence soft-caps total weight.
 */
export function querySpawnHeat(
  heat: SpawnHeatState | null | undefined,
  now: number,
  opts?: { halfLifeSec?: number; softCap?: number },
): SpawnHeatQuery {
  if (!heat || heat.samples.length === 0) {
    return { com: null, lateralSigma: 0, confidence: 0, totalWeight: 0 };
  }
  const hl = opts?.halfLifeSec ?? halfLifeSec();
  const cap = opts?.softCap ?? softCap();
  let wSum = 0;
  let sx = 0;
  let sy = 0;
  const alive: { x: number; y: number; w: number }[] = [];
  for (const s of heat.samples) {
    const w = sampleWeight(s, now, hl);
    if (w < 0.02) continue;
    alive.push({ x: s.x, y: s.y, w });
    wSum += w;
    sx += s.x * w;
    sy += s.y * w;
  }
  if (wSum < 1e-6 || alive.length === 0) {
    return { com: null, lateralSigma: 0, confidence: 0, totalWeight: 0 };
  }
  const com = { x: sx / wSum, y: sy / wSum };
  let varX = 0;
  let varY = 0;
  for (const a of alive) {
    const dx = a.x - com.x;
    const dy = a.y - com.y;
    varX += a.w * dx * dx;
    varY += a.w * dy * dy;
  }
  varX /= wSum;
  varY /= wSum;
  // Prefer the wider axis as "lateral" for screen width (wave along north wall → x).
  const lateralSigma = Math.sqrt(Math.max(varX, varY));
  const confidence = Math.max(0, Math.min(1, wSum / Math.max(1, cap)));
  return { com, lateralSigma, confidence, totalWeight: wSum };
}
